import React from 'react';
import { HelpResourceT } from '../utils/types';
import { getValueByPath } from '@utils/misc';
import { TextSmallBold } from '@as_core/elements/textStyles';
import styled from 'styled-components/macro';
import OverviewIcon from '@components/icons/overview.icon';
import DashboardIcon from '@components/icons/dashboard.icon';
import CompoundsIcon from '@components/icons/compounds.icon';
import NewCompoundIcon from '@components/icons/newCompound.icon';
import MyCompoundsIcon from '@components/icons/myCompounds.icon';
import FavoritesIcon from '@components/icons/favorites.icon';
import CompoundUniverseIcon from '@components/icons/compoundUniverse.icon';
import KinomeUniverseIcon from '@components/icons/kinomeUniverse.icon';
import UploadIcon from '@components/icons/upload.icon';
import GraphIcon from '@components/icons/graph.icon';
import DownloadFileFromS3Ref from '../DownloadFileFromS3Ref';
import OpenReferenceForDOI from '../OpenReferenceForDOI';
import { FlexItem } from '@as_core/elements/flexStyles';
import { Tooltip } from '@mui/material';
import { DocumentIconLink } from '@as_core/styles/elementStyles';
import VideoIcon from '@components/icons/video.icon';
import { Item } from './components';
import { AccountTree } from '@mui/icons-material';
import OrdersAddIcon from '@components/icons/orders.add';
import OrdersActiveIcon from '@components/icons/orders.active';
import FulfillmentActiveIcon from '@as_core/icons/fulfillmentActive.icon';
import FulfillmentConsultingIcon from '@as_core/icons/fulfillmentConsulting.icon';
import OrdersPastIcon from '@components/icons/orders.past';
import SearchIcon from '@mui/icons-material/Search';
import ListIcon from '@components/icons/list.icon';

export const SystemTypes: {value: string, label: string}[] = [
  { value: '3rnd', label: '3RnD'},
  { value: '3rnd-tools', label: '3RnD Tools'},
  { value: '3rnd-partners', label: '3RnD Partners'},
  { value: 'sciscore', label: 'SciScore'}
]

export const ProductTypes: {[key: string]: {value: string, label: string}[]} = {
  '3rnd': [
    { value: '3rnd', label: '3RnD' },
    { value: 'gostar', label: 'GOSTAR' },
    { value: 'chosmos', label: 'ChOSMOS' },
    { value: 'orders', label: 'Orders' }
  ],
  '3rnd-tools': [
    { value: 'bugs', label: 'Bug Reporting' },
    { value: 'coupons', label: 'Coupon Management' },
    { value: 'orders', label: 'Orders Management' }
  ],
  '3rnd-partners': [
    { value: 'requests', label: 'Requests Management' }
  ],
  'sciscore': [
    { value: 'sciscore', label: 'SciScore' }
  ]
}

export function getProductLabel(system: string, product: string): string {
  if (!Object.hasOwn(ProductTypes, system)) return product;
  const item = ProductTypes[system].find(i => i.value === product);
  return item ? item.label : product;
}

export const IconsForFeatureTypes: {[key: string]: React.ReactElement} = {
  '3rnd_home': <OverviewIcon />,
  overview: <DashboardIcon />,
  dashboard: <DashboardIcon />,
  aseda_compounds: <CompoundsIcon />,
  add_molecule: <NewCompoundIcon />,
  my_compounds: <MyCompoundsIcon />,
  my_favorites: <FavoritesIcon />,
  compound_universe: <CompoundUniverseIcon />,
  my_universe: <KinomeUniverseIcon />,
  upload_data: <UploadIcon />,
  my_plots: <GraphIcon />,
  manage_my_repositories: <AccountTree />,
  search: <SearchIcon />,
  past_searches: <ListIcon />,
  create_order: <OrdersAddIcon />,
  buy_credits: <OrdersAddIcon />,
  active_orders: <OrdersActiveIcon />,
  order_fulfillment: <FulfillmentActiveIcon />,
  consulting_fulfilment: <FulfillmentConsultingIcon />,
  my_completed_orders: <OrdersPastIcon />,
};

const FeaturesForSubApps: {[key: string]: string[]} = {
  '3rnd': [
    '3rnd_home', 'dashboard', 'aseda_compounds', 'add_molecule', 'my_compounds', 'my_favorites',
    'compound_universe', 'my_universe', 'upload_data', 'my_plots', 'manage_my_repositories'
  ],
  'chemporium': [
    'overview', 'search', 'create_order', 'active_orders', 'past_orders'
  ],
  'chosmos': [
    'overview', 'search', 'past_searches'
  ],
  'gostar': [
    'overview', 'search', 'create_order', 'buy_credits', 'orders', 'credit_orders'
  ],
  'orders': [
    'overview', 'create_order', 'active_orders', 'order_fulfillment', 'consulting_fulfilment', 'past_orders'
  ]
}

export function getFeatureTagLabel(feature: string): string {
  return feature
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getFeatureTagIcon(feature: string) {
  if (Object.hasOwn(IconsForFeatureTypes, feature)) return IconsForFeatureTypes[feature];
  console.error(`Cannot find icon for feature "${feature}"`);
}

export function getAllFeatures(subApp: string) {
  if (!Object.hasOwn(FeaturesForSubApps, subApp)) {
    console.error(`Cannot find any features for subApp "${subApp}"`);
    return [];
  }
  return FeaturesForSubApps[subApp];
}

export const possibleResourceValues = ['video', 'doi', 'tutorial', 'data']
const resourceMapping = {
  'video': {label: 'Video', source: 'resources', type: 'video', key: 'location_ref'},
  'doi': {label: 'Reference', source: 'resources', type: 'doi', key: 'location_ref'},
  'tutorial': {label: 'Tutorial', source: 'resources', type: 'tutorial', key: 'location_ref'},
  'data': {label: 'Data', source: 'resources', type: 'data', key: 'location_ref'},
}
export function getResourceValue(resource: HelpResourceT, field: string): string {
  if (!Object.hasOwn(resourceMapping, field)) return null;
  const fieldInfo = resourceMapping[field];
  if (fieldInfo.source === 'resources') {
    const item = resource.resources.find((r) => r.type === fieldInfo.type);
    // console.log('getResourceValue', item, field);
    if (item === undefined) return null;
    return getValueByPath(item, fieldInfo.key);
  } if (fieldInfo.source === 'value') {
    return getValueByPath(resource, fieldInfo.key, null);
  } else {
    console.error(`Unknown resource source: ${fieldInfo.source}`);
  }
  return null;
}
export function getRef(resource: HelpResourceT, field: string, openVideoModal: (v: boolean) => void) {
  // console.log('getRef', resource, field);
  if (!Object.hasOwn(resourceMapping, field)) return null;
  const fieldInfo = resourceMapping[field];
  const fieldValue = getResourceValue(resource, field);
  // console.log('getRef',fieldValue);
  if (!fieldValue) return null;
  return (
    <Item>
      {fieldInfo.label}: {getValue(field, fieldValue, openVideoModal)}
    </Item>
  );
}

export function getAvailableResources(resource: HelpResourceT) {
  let available = []
  possibleResourceValues.forEach(field => {
    if (getResourceValue(resource, field) !== null) available.push(field)
  })
  return available;
}

export function getValue(
  field: string,
  value: string | string[],
  openVideoModal?: (v: boolean) => void,
): React.ReactNode | string | null {
  const values = value as string[];
  switch (field.toLowerCase()) {
    case 'tutorial':
      if (value) {
        return <DownloadFileFromS3Ref s3Ref={value as string} />;
      }
      return null;
    case 'features':
      return (
        <Icons>
          {values.map((v: string) => (
            <Icon key={v}>{getFeatureTagIcon(v)}</Icon>
          ))}
        </Icons>
      );
    case 'data':
      return <DownloadFileFromS3Ref s3Ref={value as string} />;
    case 'doi':
      return <OpenReferenceForDOI doi={value as string} />;
    case 'name':
      return <TextSmallBold color={'primary'}>{value as string}</TextSmallBold>;
    case 'video':
      return(
        <FlexItem>
          <Tooltip
            key={`show`}
            title={`Open window to show video`}
            placement={'bottom-start'}
            arrow
          >
            <DocumentIconLink
              onClick={() => openVideoModal(true)}
              isActive={true}
            >
              <VideoIcon />
            </DocumentIconLink>
          </Tooltip>
        </FlexItem>
      );
  }
  return null;
}

const Icons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 10px;
`;

const Icon = styled.div<{
  children: React.ReactNode;
}>`
  display: flex;
  padding-left: 5px;
  width: 24px;
`;
